/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService, MerchantService, NotificationService } from "../../../services";
import { MerchantDetailsParentComponent } from "../merchant-details-parent.component";
import { Merchant, AccountUsageTypes } from "../../../models";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { accountNumberValidation } from "../../../validators/MPCustomValidator";

@Component({
  selector: "app-account-details",
  templateUrl: "./account-details.component.html"
})
export class AccountDetailsComponent extends MerchantDetailsParentComponent implements OnInit {
  accountDetailsForm!: FormGroup;
  accountUsage = AccountUsageTypes;
  editable = false;
  actionInProgress = false;
  buttonName = "Update Bank Account Details";
  @Input("merchant") merchant!: Merchant;

  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    merchantService: MerchantService,
    notificationService: NotificationService,
    authService: AuthService,
    private fb: FormBuilder
  ) {
    super(router, activatedRoute, merchantService, notificationService, authService);
  }

  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.accountDetailsForm = this.fb.group(
      {
        accountDetails: this.fb.group({
          federalTaxId: [
            this.merchant.accountDetails.federalTaxId,
            [Validators.required, Validators.pattern("^([0-9]{9})$")]
          ],
          accountUsage: [
            this.merchant.accountDetails.separateFundingAccounts ? "FundingDeposits" : this.accountUsage.Combined,
            Validators.required
          ],
          accountType: [this.merchant.accountDetails.accountType, Validators.required],
          checkingDepositType: [this.merchant.accountDetails.checkingDepositType, Validators.required],
          routingNumber: [
            this.merchant.accountDetails.routingNumber,
            [Validators.required, Validators.pattern("^([0-9]{9})$")]
          ],
          accountNumber: [
            this.merchant.accountDetails.accountNumber,
            [Validators.required, Validators.pattern("[0-9]+$"), Validators.maxLength(49)]
          ]
        }),
        otherAccountDetails: this.fb.group({
          accountUsage: ["FeeWithdrawals", Validators.required],
          accountType: [
            this.merchant.accountDetails.feeAccountDetails?.accountType
              ? this.merchant.accountDetails.feeAccountDetails?.accountType
              : "",
            Validators.required
          ],
          routingNumber: [
            this.merchant.accountDetails.feeAccountDetails?.routingNumber,
            [Validators.required, Validators.pattern("^([0-9]{9})$")]
          ],
          accountNumber: [
            this.merchant.accountDetails.feeAccountDetails?.accountNumber,
            [Validators.required, Validators.pattern("[0-9]+$"), Validators.maxLength(49)]
          ],
          checkingDepositType: [""]
        })
      },
      { validators: accountNumberValidation() }
    );
  }

  get accountDetailsFormGroup(): FormGroup {
    return this.accountDetailsForm.controls["accountDetails"] as FormGroup;
  }

  get otherAccountDetailsFormGroup(): FormGroup {
    return this.accountDetailsForm.controls["otherAccountDetails"] as FormGroup;
  }

  updateElementMerchant() {
    const isCombined: boolean = this.accountDetailsFormGroup.getRawValue().accountUsage === this.accountUsage.Combined;
    let isFormValid: boolean;
    if (isCombined) {
      isFormValid = this.accountDetailsFormGroup.valid;
    } else {
      isFormValid = this.accountDetailsFormGroup.valid && this.otherAccountDetailsFormGroup.valid;
    }
    if (isFormValid) {
      this.actionInProgress = true;
      const tempMerchant: Merchant = JSON.parse(JSON.stringify(this.merchant));
      tempMerchant.accountDetails.federalTaxId = this.accountDetailsFormGroup.get("federalTaxId")?.value;
      if (isCombined) {
        tempMerchant.accountDetails.separateFundingAccounts = false;
        AccountDetailsComponent.setMerchantAccountDetails(tempMerchant, this.accountDetailsFormGroup);
        delete tempMerchant.accountDetails.feeAccountDetails;
      } else {
        if (this.accountDetailsFormGroup.getRawValue().accountUsage === "FeeWithdrawals") {
          AccountDetailsComponent.setMerchantFeeDetails(tempMerchant, this.accountDetailsFormGroup);
          AccountDetailsComponent.setMerchantAccountDetails(tempMerchant, this.otherAccountDetailsFormGroup);
        } else if (this.otherAccountDetailsFormGroup.getRawValue().accountUsage === "FeeWithdrawals") {
          AccountDetailsComponent.setMerchantFeeDetails(tempMerchant, this.otherAccountDetailsFormGroup);
          AccountDetailsComponent.setMerchantAccountDetails(tempMerchant, this.accountDetailsFormGroup);
        }
      }
      tempMerchant.statementOptions = {};
      this.merchantService.updateElementMerchant("BANK_ACCOUNT", tempMerchant).subscribe(
        (merchant) => {
          this.merchant = merchant;
          this.editable = false;
          this.actionInProgress = false;
        },
        (error) => {
          this.notificationService.error(error.error);
          this.actionInProgress = false;
        }
      );
    } else {
      this.accountDetailsForm.markAllAsTouched();
      this.actionInProgress = false;
    }
  }

  private static setMerchantAccountDetails(merchant: Merchant, accountDetails: FormGroup) {
    merchant.accountDetails.accountType = accountDetails.getRawValue().accountType;
    merchant.accountDetails.accountType === "C"
      ? (merchant.accountDetails.checkingDepositType = accountDetails.getRawValue().checkingDepositType)
      : (merchant.accountDetails.checkingDepositType = "CommercialChecking");
    merchant.accountDetails.routingNumber = accountDetails.getRawValue().routingNumber;
    merchant.accountDetails.accountNumber = accountDetails.getRawValue().accountNumber;
  }

  private static setMerchantFeeDetails(merchant: Merchant, accountDetails: FormGroup) {
    merchant.accountDetails.separateFundingAccounts = true;
    merchant.accountDetails.feeAccountDetails = {
      accountType: accountDetails.getRawValue().accountType,
      routingNumber: accountDetails.getRawValue().routingNumber,
      accountNumber: accountDetails.getRawValue().accountNumber
    };
  }

  setEditable(value: boolean) {
    this.editable = value;
  }

  receiveEditEvent(value: boolean) {
    this.editable = value;
    this.createForm();
  }
}
